import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCameraRecovery } from '@renderer/pages/View'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import cogoToast from '@successtar/cogo-toast'
import {
  restoreOriginalSettings,
  applyLiveViewBrightness
} from '@renderer/utils/cameras/cameraSettingsManager'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { getPhotoLayoutInfo, validateFrameSelection } from '@renderer/utils/frameBasedUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay } from '@renderer/utils/lutUtils'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { Button } from '@renderer/components'
import { useTranslation } from 'react-i18next'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'
import { PREVIEW_CAPTURE_SHARE_FRAME_STRIP } from '@renderer/utils/previewCaptureShare'
import PrintPreview from '@renderer/components/PrintPreview'
import FrameOrderedLiveLayers from '@renderer/components/FrameOrderedLiveLayers'
import { FaExchangeAlt } from 'react-icons/fa'
import { useDesignLayout } from '@renderer/hooks'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'

const FrameBasedCapture: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    setSessionState,
    userPreferences,
    gallery,
    galleryKey,
    logActivity,
    userId
  } = useSessionStateStore()
  const { config: globalConfig, frames, getFrame, filters } = useConfigStore()
  const { downloadLocal, cameraSettings, selectedCamera: selectedCameraId } = useSettingsStore()
  const cameraRecoveryContext = useCameraRecovery()
  const {
    designW: layoutCanvasW,
    designH: layoutCanvasH,
    isLandscape,
    isCompact,
    isTiny
  } = useDesignLayout()

  // Get selected filter from user preferences, fallback to first filter
  const selectedFilterId = userPreferences.filterId
  const selectedFilter = selectedFilterId
    ? filters.find((f) => f._id === selectedFilterId) || filters[0]
    : filters[0]
  const filterAdjustments = selectedFilter?.adjustments
  const applyAfterCapture =
    globalConfig?.pages.filter?.settings?.applyAfterCapture ?? false

  // Get selected frame
  const selectedFrame = getFrame(userPreferences.frameId || '')
  if (!selectedFrame) {
    navigate(`/${projectId}/frame`)
    return null
  }

  // Get unique photo types from frame (like newspaper/Capture.tsx)
  const uniquePhotoTypes = new Set(
    selectedFrame.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size

  // Convert Set to sorted array for indexing (numeric-aware: photo-2 < photo-10)
  const uniquePhotoTypesArray = Array.from(uniquePhotoTypes).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  )

  // Track captured photos by type (like newspaper/Capture.tsx)
  const [currentPhotoTypeIndex, setCurrentPhotoTypeIndex] = useState<number>(0)
  const [capturedPhotosByType, setCapturedPhotosByType] = useState<Map<string, string>>(new Map())

  // Get photo ratio from frame
  const getPhotoRatio = (slot: FrameOption['drawParams']['images'][number]): number => {
    return slot.width / slot.height
  }

  // Apply filter function (skip during capture when filter is applied post-capture)
  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (applyAfterCapture) return
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (filterAdjustments) {
      filterUtils.applyAdjustments(ctx, filterAdjustments)
    }
  }

  // Delegate to shared helper (adds Number.isFinite + positive guard to prevent NaN/Infinity slipping through).
  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  // Configuration
  const config = globalConfig?.pages.capture
  const captureSettings = config?.settings

  // Frame-based specific timers with fallback to standard timers
  const prepTimeout =
    captureSettings?.frameBasedPrepTimeout ?? captureSettings?.prepTimeout ?? 5
  const captureCountdownSeconds =
    captureSettings?.frameBasedCaptureCountdown ?? captureSettings?.captureCountdown ?? 3
  const captureTimeout =
    captureSettings?.frameBasedCaptureTimeout ?? captureSettings?.captureTimeout ?? 300

  // Validate frame selection
  if (!validateFrameSelection(userPreferences.frameId)) {
    console.error('Frame must be selected before capture')
    logActivity('Frame selection required before capture', { level: 'error' })
    navigate(`/${projectId}/frame`)
    return null
  }

  // Get photo count and layout based on selected frame
  const photoLayoutInfo = getPhotoLayoutInfo(userPreferences.frameId, frames)
  const captureCount = Math.max(uniquePhotoCount, photoLayoutInfo.count)

  const [captureCountdown, setCaptureCountdown] = useState<number>(captureCountdownSeconds)
  const [preparing, setPreparing] = useState(userPreferences.retakeCount === 0)
  const [capturing, setCapturing] = useState(false)
  const [allCapturesComplete, setAllCapturesComplete] = useState(false)
  const [isCaptureError, setIsCaptureError] = useState(false)
  const [countdownKey, setCountdownKey] = useState(0)
  const [mirroredPhotoTypes, setMirroredPhotoTypes] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)
  const [isRecoveringCamera, setIsRecoveringCamera] = useState(false)
  const [isPreviewPaused, setIsPreviewPaused] = useState(false)

  // Use shared countdown instead of timeout countdown
  const {
    remainingTime: sharedRemainingTime,
    isExpired: isSharedCountdownExpired,
    startCountdown: startSharedCountdown
  } = useSharedCountdown()
  const FPS = 30
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)
  const sessionCanvasRecorderRef = useRef<Recorder | null>(null)
  const sessionStepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** Dimensions the current recorder was encoded with. Used for drift checks before start(). */
  const recorderEncoderDimsRef = useRef<{ w: number; h: number } | null>(null)
  const recordingCanvasElRef = useRef<HTMLCanvasElement | null>(null)
  const [recordingEncoderDims, setRecordingEncoderDims] = useState<{
    width: number
    height: number
  } | null>(null)
  const onRecordingCanvasReady = useCallback(
    (el: HTMLCanvasElement, _ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => {
      recordingCanvasElRef.current = el
      setRecordingEncoderDims({ width: dim.width, height: dim.height })
    },
    []
  )

  // Refs
  const stopCaptureRef = useRef<boolean>(false)
  const recordingActiveRef = useRef<boolean>(false)
  const recordingPromiseRef = useRef<Promise<void> | null>(null)
  const currentPhotoTypeIndexRef = useRef<number>(0)
  const captureLockRef = useRef<boolean>(false)
  const isRecoveringRef = useRef(false)
  const previewReadyAfterRecoveryRef = useRef<boolean>(false)
  const handleCaptureRef = useRef<(() => Promise<string | undefined>) | null>(null)

  // Countdown hooks
  const prepCountdownHook = useCountdown(
    userPreferences.retakeCount > 0 ? 1 : prepTimeout
  )
  const captureCountdownHook = useCountdown(captureCountdownSeconds)
  const initialCaptureCountdown = config?.settings.captureCountdown || 3

  // Frame settings — match standard Capture / Retake (settings ratio + frame preference)
  /** Preview L3/L2 highlight only; omit when captureRatio is auto and no frame. */
  const previewHighlightRatio =
    userPreferences.framePhotoRatio ?? getNormalizedCaptureRatio() ?? undefined

  // Video recording configuration
  const useRecording = userPreferences.useRecording
  const recordingSpeed = globalConfig?.pages.capture?.settings?.recordingSpeed || 1
  const targetDuration = (captureCountdownSeconds || 3) * 1000
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)

  // Helper to validate dimensions for H.264 codec (requires even numbers)
  const validateDimensions = (width: number, height: number): { width: number; height: number } => {
    // Round down to nearest even number
    const validatedWidth = Math.floor(width / 2) * 2
    const validatedHeight = Math.floor(height / 2) * 2
    return { width: validatedWidth, height: validatedHeight }
  }

  // Portrait uses a hidden full-size Preview producer (same as landscape) — onCanvasReady sets dimensions.

  // Monitor preview readiness after recovery (canvas dimensions update when stream resumes)
  useEffect(() => {
    if (isRecoveringRef.current && canvasDimensions) {
      console.log('[FrameBasedCapture RECOVERY] Preview is ready after recovery')
      previewReadyAfterRecoveryRef.current = true
    }
  }, [canvasDimensions])

  // Initialize recorders when rotation-baked recording canvas is ready (Preview onRecordingCanvasReady)
  useEffect(() => {
    const rec = recordingCanvasElRef.current
    const recCtx = rec?.getContext('2d')
    if (!useRecording || !recCtx || !rec || !recordingEncoderDims || recordingEncoderDims.width <= 0) {
      return
    }

    const bw = rec.width
    const bh = rec.height
    const validatedDims = validateDimensions(
      bw > 0 ? bw : recordingEncoderDims.width,
      bh > 0 ? bh : recordingEncoderDims.height
    )
    recorderEncoderDimsRef.current = { w: validatedDims.width, h: validatedDims.height }

    const stripMode =
      userPreferences.recordingType === 'strip_live' ||
      userPreferences.recordingType === 'strip_story'
    const sessionMode = userPreferences.recordingType === 'session_recording'

    if (stripMode) {
      if (captureCanvasRecorderRef.current) {
        const currentStatus = captureCanvasRecorderRef.current.status
        if (currentStatus === RecorderStatus.Recording) {
          console.log(
            `[FrameBasedCapture] Capture recorder already Recording, skipping creation in useEffect`
          )
          return
        }
        if (currentStatus === RecorderStatus.Ready) {
          console.log(
            `[FrameBasedCapture] Capture recorder already Ready, keeping existing recorder`
          )
          return
        }
        try {
          captureCanvasRecorderRef.current.stop()
        } catch {
          /* ignore */
        }
        captureCanvasRecorderRef.current = null
      }

      try {
        captureCanvasRecorderRef.current = new Recorder(recCtx as unknown as RenderingContext, {
          name: `dreambooth-${galleryKey || gallery.id}-capture`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDims.width,
            height: validatedDims.height,
            bitrate: 2000000
          },
          duration: Infinity,
          download: downloadLocal && stripMode,
          frameRate: FPS
        })
        console.log(
          `[FrameBasedCapture] Capture recorder created ${validatedDims.width}x${validatedDims.height}`
        )
      } catch (error) {
        console.error('[FrameBasedCapture] Error creating capture recorder:', error)
        captureCanvasRecorderRef.current = null
      }
    }

    if (sessionMode) {
      if (sessionCanvasRecorderRef.current) {
        try {
          sessionCanvasRecorderRef.current.stop()
        } catch {
          /* ignore */
        }
        sessionCanvasRecorderRef.current = null
      }
      try {
        sessionCanvasRecorderRef.current = new Recorder(recCtx as unknown as RenderingContext, {
          name: `dreambooth-${galleryKey || gallery.id}-session`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDims.width,
            height: validatedDims.height,
            bitrate: 2000000,
            latencyMode: 'realtime'
          },
          duration: Infinity,
          download: downloadLocal && sessionMode,
          frameRate: FPS
        })
        console.log(
          `[FrameBasedCapture] Session recorder created ${validatedDims.width}x${validatedDims.height}`
        )
      } catch (error) {
        console.error('[FrameBasedCapture] Error creating session recorder:', error)
        sessionCanvasRecorderRef.current = null
      }
    }

    return (): void => {
      /* Do not stop recorders here — dims effect re-runs would interrupt active capture */
    }
  }, [
    recordingEncoderDims,
    useRecording,
    userPreferences.recordingType,
    downloadLocal,
    galleryKey,
    gallery.id,
    FPS
  ])

  useEffect(() => {
    return (): void => {
      if (sessionStepIntervalRef.current) {
        clearInterval(sessionStepIntervalRef.current)
        sessionStepIntervalRef.current = null
      }
      try {
        sessionCanvasRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
      try {
        captureCanvasRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
    }
  }, [])

  // Frame recording loop - records frames during countdown
  useEffect(() => {
    if (!capturing || !useRecording || !captureCanvasRecorderRef.current || currentPhotoTypeIndex >= uniquePhotoTypesArray.length) {
      recordingActiveRef.current = false
      recordingPromiseRef.current = null
      return
    }

    // Only record if recorder is in Recording status
    if (captureCanvasRecorderRef.current.status !== RecorderStatus.Recording) {
      return
    }

    recordingActiveRef.current = true

    const recordingLoop = async (): Promise<void> => {
      let frameCount = 0
      const startTime = performance.now()

      while (
        recordingActiveRef.current &&
        frameCount < totalFrames &&
        captureCanvasRecorderRef.current &&
        captureCanvasRecorderRef.current.status === RecorderStatus.Recording
      ) {
        const currentTime = performance.now()
        const elapsedTime = currentTime - startTime
        const targetFrameTime = (frameCount * 1000) / FPS

        if (elapsedTime >= targetFrameTime) {
          try {
            await captureCanvasRecorderRef.current.step()
            frameCount++
            if (frameCount % 30 === 0) {
              console.log(`[FrameBasedCapture] Recorded ${frameCount}/${totalFrames} frames for photo ${currentPhotoTypeIndex + 1}`)
            }
          } catch (error) {
            console.error('[FrameBasedCapture] Error stepping recorder', error)
          }
        }

        // Small delay to prevent CPU overload
        await new Promise((resolve) => setTimeout(resolve, 1))
      }

      if (frameCount > 0) {
        console.log(`[FrameBasedCapture] Finished recording frames for photo ${currentPhotoTypeIndex + 1}: ${frameCount}/${totalFrames} frames recorded`)
      }
    }

    recordingPromiseRef.current = recordingLoop()
    recordingPromiseRef.current.catch((error) => {
      console.error('[FrameBasedCapture] Recording loop error', error)
    })

    return (): void => {
      recordingActiveRef.current = false
    }
  }, [
    capturing,
    useRecording,
    FPS,
    totalFrames,
    recordingSpeed,
    currentPhotoTypeIndex,
    uniquePhotoTypesArray.length
  ])



  // Initialize timeouts and countdowns
  useEffect((): (() => void) | void => {
    stateUploader.uploadSessionState()

    // Start shared countdown if not already started
    if (!useSessionStateStore.getState().userPreferences.sharedCountdownStartTime) {
      startSharedCountdown()
      logActivity('Shared countdown started on FrameBasedCapture page', {
        duration: captureTimeout
      })
    }

    // Set up prep countdown callbacks
    prepCountdownHook.setOnComplete(() => {
      startCaptureSession()
    })

    // Set up capture countdown
    captureCountdownHook.setOnTick(() => {
      if (isRecoveringRef.current) return
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      if (isRecoveringRef.current) {
        console.log('[FrameBasedCapture COUNTDOWN] Recovery in progress, skipping capture')
        return
      }
      void handleCaptureRef.current?.()
    })

    prepCountdownHook.start()

    return (): void => {
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true
    }
  }, [])

  // Navigate away if countdown expired
  useEffect(() => {
    if (isSharedCountdownExpired) {
      logActivity('Shared countdown expired on FrameBasedCapture page')
      if (capturedPhotosByType.size > 0) {
        navigate(`/${projectId}/retake`)
      } else {
        navigate(`/${projectId}/frame`)
      }
    }
  }, [isSharedCountdownExpired, capturedPhotosByType.size])

  const startCaptureSession = async (): Promise<void> => {
    stopCaptureRef.current = false
    logActivity('Capture session started!', {
      captureTime: config?.settings.captureCountdown || 3,
      prepTime: userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
    })

    try {
      prepCountdownHook.stop()
      setPreparing(false)
      runCaptureLoop()
    } catch (error) {
      console.error('Error in startCaptureSession:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Error in startCaptureSession: ${errorMessage}`, { level: 'error' })
    }
  }

  // Keep ref in sync so handleCapture always reads the latest index
  useEffect(() => {
    currentPhotoTypeIndexRef.current = currentPhotoTypeIndex
  }, [currentPhotoTypeIndex])

  /** Reset countdown, set fresh callbacks, and start. Eliminates the race
   *  condition where `setOnComplete` via useEffect might lag behind `start()`. */
  const startCaptureCountdown = (): void => {
    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.stop()
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)
    setCountdownKey((prev) => prev + 1)

    captureCountdownHook.setOnTick(() => {
      if (isRecoveringRef.current) return
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })
    captureCountdownHook.setOnComplete(() => {
      if (isRecoveringRef.current) {
        console.log('[FrameBasedCapture COUNTDOWN] Recovery in progress, skipping capture')
        return
      }
      void handleCaptureRef.current?.()
    })

    setCapturing(true)
    captureCountdownHook.start()
  }

  const onCameraReconnected = (): void => {
    console.log('[FrameBasedCapture RECOVERY] Camera reconnected, resuming capture')
    isRecoveringRef.current = false
    previewReadyAfterRecoveryRef.current = false
    setIsPreviewPaused(false)
    setIsRecoveringCamera(false)
    setIsCaptureError(false)
    setIsCapturingWithShutter(false)

    cogoToast.success(
      t('capture.cameraReconnected', 'Camera reconnected successfully. Resuming capture...')
    )

    logActivity('Camera recovery completed, resuming frame-based capture', {
      photoTypeIndex: currentPhotoTypeIndexRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)

    window.setTimeout(() => {
      startCaptureCountdown()
    }, 800)
  }

  const handleCameraRecovery = async (): Promise<void> => {
    if (isRecoveringCamera) {
      console.log('[RECOVERY] Recovery already in progress')
      return
    }

    console.log('[RECOVERY] Starting camera recovery process (frame-based)')
    previewReadyAfterRecoveryRef.current = false
    isRecoveringRef.current = true
    setIsRecoveringCamera(true)
    setIsPreviewPaused(true)
    setIsCaptureError(false)
    setCapturing(false)
    setIsCapturingWithShutter(false)
    captureCountdownHook.stop()
    captureCountdownHook.setOnComplete(null)
    setCaptureCountdown(initialCaptureCountdown)

    logActivity('Camera recovery process started (frame-based)', {
      photoTypeIndex: currentPhotoTypeIndexRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    const preservedSelectedCamera = selectedCameraId
    console.log('[RECOVERY] Preserved selectedCamera:', preservedSelectedCamera)

    if (!preservedSelectedCamera) {
      console.error('[RECOVERY] No selectedCamera found, cannot recover')
      cogoToast.error(
        t(
          'capture.recoveryNoCamera',
          'No camera selected. Please select a camera in settings and try again.'
        ),
        { hideAfter: 5 }
      )
      setIsRecoveringCamera(false)
      setIsPreviewPaused(false)
      isRecoveringRef.current = false
      return
    }

    try {
      console.log('[RECOVERY] Attempting simple reconnect first (no disconnect)')
      cogoToast.info(t('capture.recoveryReconnecting', 'Reconnecting camera...'))

      try {
        const { setSelectedCamera } = useSettingsStore.getState()
        if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
          console.log(
            '[RECOVERY] Restoring selectedCamera before reconnect:',
            preservedSelectedCamera
          )
          setSelectedCamera(preservedSelectedCamera)
        }

        let deviceType: 'webcam' | 'camera' = 'webcam'
        let params: string | { productId?: number; vendorId?: number } = preservedSelectedCamera

        if (!isNaN(Number(preservedSelectedCamera))) {
          deviceType = 'camera'
          params = { productId: Number(preservedSelectedCamera) }
        } else if (preservedSelectedCamera === 'detect') {
          deviceType = 'camera'
          params = {}
        }

        const reconnectPromise = unifiedCameraUtils.tryToConnect(
          deviceType,
          params,
          userPreferences.selectedCamera as 'primary' | 'secondary'
        )
        const timeoutPromise = new Promise((_resolve, reject) => {
          setTimeout(() => {
            reject(new Error('Reconnection timed out'))
          }, 5000)
        })

        await Promise.race([reconnectPromise, timeoutPromise])

        console.log('[RECOVERY] Simple reconnect successful!')
        cogoToast.success(t('capture.recoveryReconnected', 'Camera reconnected!'), { hideAfter: 2 })

        onCameraReconnected()
        return
      } catch (reconnectError) {
        console.log(
          '[RECOVERY] Simple reconnect failed, will disconnect and reconnect:',
          reconnectError
        )
      }

      console.log('[RECOVERY] Disconnecting camera before reconnection')
      cogoToast.info(t('capture.recoveryDisconnecting', 'Disconnecting camera...'), {
        hideAfter: 2
      })

      try {
        await new Promise((resolve) => setTimeout(resolve, 500))
        await unifiedCameraUtils.disconnect(userPreferences.selectedCamera as 'primary' | 'secondary')
        console.log('[RECOVERY] Camera disconnected')
      } catch (disconnectError) {
        console.warn('[RECOVERY] Camera disconnect had issues, continuing:', disconnectError)
      }

      const { setSelectedCamera } = useSettingsStore.getState()
      if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
        console.log(
          '[RECOVERY] Restoring selectedCamera after disconnect:',
          preservedSelectedCamera
        )
        setSelectedCamera(preservedSelectedCamera)
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        console.log('[RECOVERY] Triggering CameraPrompt for user reconnection')
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        console.error('[RECOVERY] onCameraRecoveryRequest not available in context')
        cogoToast.error(
          t(
            'capture.recoveryContextError',
            'Recovery system error. Please reconnect camera manually.'
          )
        )
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
        isRecoveringRef.current = false
      }
    } catch (error) {
      console.error('[RECOVERY] Error during camera recovery:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(
        t('capture.recoveryError', 'Camera recovery failed. Please reconnect manually.'),
        { hideAfter: 5 }
      )
      logActivity(`Camera recovery error: ${errorMessage}`, {
        error: errorMessage,
        photoTypeIndex: currentPhotoTypeIndexRef.current,
        level: 'error'
      })
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
        isRecoveringRef.current = false
      }
    }
  }

  const stopSessionRecordingAndUpload = async (): Promise<void> => {
    if (sessionStepIntervalRef.current) {
      clearInterval(sessionStepIntervalRef.current)
      sessionStepIntervalRef.current = null
    }
    if (!useRecording || userPreferences.recordingType !== 'session_recording') return
    if (!sessionCanvasRecorderRef.current) return
    try {
      const recordedChunks = await sessionCanvasRecorderRef.current.stop()
      const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const mp4Url = URL.createObjectURL(mp4Blob)
      const videoFile = new File([mp4Blob], `dreambooth-${gallery.id}-session.mp4`, {
        type: 'video/mp4'
      })
      backgroundVideoProcessor.processVideo({
        id: `session-${gallery.id}-${Date.now()}`,
        type: 'session',
        resources: [],
        frameConfig: {
          id: 'capture-video',
          baseImage: '',
          drawParams: {
            canvasWidth: canvasDimensions?.width || 1200,
            canvasHeight: canvasDimensions?.height || 1800,
            backgroundColor: '#FFFFFF',
            images: []
          }
        },
        sessionId: gallery.id,
        userId: userId || '',
        projectId: projectId || '',
        galleryId: gallery.id,
        videoFile,
        onComplete: () => {
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          logActivity('Session recording uploaded.', { type: 'recording' })
          setSessionState({
            gallery: {
              capturedItems: [
                ...prevItems,
                {
                  id: 'dreambooth-session-recording-' + new Date().getTime(),
                  recordingUrl: mp4Url
                }
              ]
            }
          })
        },
        onError: (error) => {
          console.error('Error processing session video:', error)
          cogoToast.error(
            t('capture.errorProcessingSessionVideo', 'Error processing session video')
          )
        }
      })
    } catch (e) {
      console.error('[FrameBasedCapture] stopSessionRecording:', e)
    }
    sessionCanvasRecorderRef.current = null
  }

  const runCaptureLoop = async (): Promise<void> => {
    console.log('[FrameBasedCapture] Starting capture loop for photo types:', uniquePhotoTypesArray.length)

    if (useRecording && userPreferences.recordingType === 'session_recording' && sessionCanvasRecorderRef.current) {
      try {
        const recEl =
          recordingCanvasElRef.current ||
          (document.querySelector(
            'canvas[data-preview-type="camera-recording"]'
          ) as HTMLCanvasElement | null)
        const encDims = recorderEncoderDimsRef.current
        if (recEl && encDims && (recEl.width !== encDims.w || recEl.height !== encDims.h)) {
          console.warn(
            `[FrameBasedCapture] session encoder (${encDims.w}x${encDims.h}) diverged from recording canvas (${recEl.width}x${recEl.height}) before start`
          )
        }
        await sessionCanvasRecorderRef.current.start()
        const stepInterval = 1000 / (FPS / recordingSpeed)
        sessionStepIntervalRef.current = setInterval(async () => {
          if (sessionCanvasRecorderRef.current?.status !== RecorderStatus.Recording) return
          try {
            await sessionCanvasRecorderRef.current?.step()
          } catch (err) {
            console.error('[FrameBasedCapture] session recorder step', err)
          }
        }, stepInterval)
      } catch (error) {
        console.error('[FrameBasedCapture] Error starting session recorder', error)
      }
    }

    if (useRecording && captureCanvasRecorderRef.current) {
      try {
        console.log('[FrameBasedCapture] Starting capture strip recorder for photo 1 before countdown')
        const recEl =
          recordingCanvasElRef.current ||
          (document.querySelector(
            'canvas[data-preview-type="camera-recording"]'
          ) as HTMLCanvasElement | null)
        const encDims = recorderEncoderDimsRef.current
        if (recEl && encDims && (recEl.width !== encDims.w || recEl.height !== encDims.h)) {
          console.warn(
            `[FrameBasedCapture] capture encoder (${encDims.w}x${encDims.h}) diverged from recording canvas (${recEl.width}x${recEl.height}) before start`
          )
        }
        await captureCanvasRecorderRef.current.start()
        const recorderStatus = captureCanvasRecorderRef.current.status
        if (recorderStatus !== RecorderStatus.Recording) {
          console.error(
            `[FrameBasedCapture] Recorder failed to start for photo 1, status: ${recorderStatus}`
          )
        } else {
          console.log(
            `[FrameBasedCapture] Successfully started recorder for photo 1, status: ${recorderStatus}`
          )
        }
      } catch (error) {
        console.error('[FrameBasedCapture] Error starting capture recorder', error)
      }
    }

    startCaptureCountdown()
  }

  const handleCapture = async (): Promise<string | undefined> => {
    if (captureLockRef.current) {
      console.log('[FrameBasedCapture] Capture already in progress, skipping duplicate')
      return undefined
    }
    captureLockRef.current = true

    const currentPhotoTypeIndex = currentPhotoTypeIndexRef.current

    if (currentPhotoTypeIndex >= uniquePhotoTypesArray.length || stopCaptureRef.current) {
      setAllCapturesComplete(true)
      setCapturing(false)
      if (!stopCaptureRef.current) {
        logActivity('Initial capture session completed.')
        await stopSessionRecordingAndUpload()
        stateUploader.uploadSessionState()
        navigate(`/${projectId}/retake`)
      }
      captureLockRef.current = false
      return undefined
    }

    const currentPhotoType = uniquePhotoTypesArray[currentPhotoTypeIndex]
    const cameraKey = userPreferences.selectedCamera as 'primary' | 'secondary'

    const isLastPhoto = currentPhotoTypeIndex >= uniquePhotoTypesArray.length - 1
    // We can resume the next photo's countdown the instant the raw frame is grabbed — but
    // only when there is no per-capture recorder to finalize and recreate between shots
    // (that pipeline is timing-sensitive and must stay sequential).
    const canDecoupleNextCountdown = !useRecording && !isLastPhoto
    let advancedToNextCountdown = false

    try {
      console.log(
        `Starting frame-based capture process for photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${uniquePhotoTypesArray.length})`
      )

      flashEffect('.flash')
      setCapturing(false)
      setIsCaptureError(false)

      if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType === 'camera') {
        setIsCapturingWithShutter(true)
      }

      if (cameraSettings.autoBrightenLiveViewForFlash && cameraSettings.useShutterCapture) {
        try {
          await restoreOriginalSettings(cameraKey)
          console.log('[FrameBasedCapture] Restored original camera settings before capture')
        } catch (err) {
          console.error('[FrameBasedCapture] Failed to restore original settings:', err)
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 200))

      // Fired by captureImage once the raw frame is in hand and the camera is free. In the
      // no-recording path this immediately advances to the next photo's countdown while the
      // transforms / brightness restore / gallery write below finish in the background.
      const onRawCaptured = (): void => {
        // The shutter has fired and the photo is taken — clear the "stay still" overlay so
        // it doesn't cover the next countdown while transforms finish in the background.
        setIsCapturingWithShutter(false)
        if (!canDecoupleNextCountdown) return
        if (isRecoveringRef.current || stopCaptureRef.current) return
        advancedToNextCountdown = true
        const nextIndex = currentPhotoTypeIndex + 1
        setCurrentPhotoTypeIndex(nextIndex)
        currentPhotoTypeIndexRef.current = nextIndex
        setPreparing(false)
        // Release the lock so the next countdown's capture isn't rejected as a duplicate
        // while this photo is still being processed in the background.
        captureLockRef.current = false
        startCaptureCountdown()
      }

      let imageData: File | undefined
      try {
        imageData = await unifiedCameraUtils.captureImage(
          cameraKey,
          applyAfterCapture ? (): void => {} : applyFilter,
          getNormalizedCaptureRatio(),
          !cameraSettings.useShutterCapture,
          onRawCaptured
        )
      } finally {
        setIsCapturingWithShutter(false)
      }

      // Stop frame recording and wait for it to finish (recording mode only).
      if (useRecording) {
        recordingActiveRef.current = false
        if (recordingPromiseRef.current) {
          await recordingPromiseRef.current
        }
        await new Promise((resolve) => setTimeout(resolve, 100)) // Small delay to ensure frames finish
      }

      // Stop and save recording
      let captureUrl: string | undefined = undefined
      if (useRecording && captureCanvasRecorderRef.current) {
        const recorderStatus = captureCanvasRecorderRef.current.status
        console.log(`[FrameBasedCapture] Stopping recorder for photo ${currentPhotoTypeIndex + 1}, current status: ${recorderStatus}`)
        
        if (recorderStatus === RecorderStatus.Recording) {
          try {
            const captureChunks = await captureCanvasRecorderRef.current.stop()
            if (!captureChunks) {
              console.warn(`[FrameBasedCapture] Recorder returned no chunks for photo ${currentPhotoTypeIndex + 1}`)
            } else {
              const chunksArray = Array.isArray(captureChunks) 
                ? captureChunks 
                : [captureChunks as ArrayBuffer]
              const chunksLength = Array.isArray(captureChunks) 
                ? captureChunks.length 
                : (captureChunks as ArrayBuffer).byteLength
              console.log(`[FrameBasedCapture] Recorder stopped for photo ${currentPhotoTypeIndex + 1}, chunks length: ${chunksLength}`)
              const captureBlob = new Blob(chunksArray as BlobPart[], { type: 'video/mp4' })
              console.log(`[FrameBasedCapture] Created blob for photo ${currentPhotoTypeIndex + 1}, size: ${captureBlob.size} bytes`)
              captureUrl = URL.createObjectURL(captureBlob)
              console.log(`[FrameBasedCapture] Created video blob URL for photo ${currentPhotoTypeIndex + 1}: ${captureUrl}, size: ${captureBlob.size} bytes`)
            }
          } catch (error) {
            console.error(`[FrameBasedCapture] Error stopping capture recorder for photo ${currentPhotoTypeIndex + 1}:`, error)
          }
        } else {
          console.warn(`[FrameBasedCapture] Recorder was not recording for photo ${currentPhotoTypeIndex + 1} (status: ${recorderStatus}), skipping video creation.`)
        }
      }

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)

        if (cameraSettings.autoBrightenLiveViewForFlash && cameraSettings.useShutterCapture) {
          try {
            await applyLiveViewBrightness(cameraKey, {
              autoBrightenLiveViewForFlash: cameraSettings.autoBrightenLiveViewForFlash,
              liveViewShutterSpeedOverride: cameraSettings.liveViewShutterSpeedOverride
            })
            console.log('[FrameBasedCapture] Re-applied live view brightness settings after capture')
          } catch (err) {
            console.error('[FrameBasedCapture] Failed to re-apply live view brightness:', err)
          }
        }

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${galleryKey || gallery.id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          logActivity(`Downloaded photo`, {
            type: 'photo',
            filename: `dreambooth-${galleryKey || gallery.id}-${Date.now()}.jpg`
          })
        }

        setCapturedPhotosByType((prev) => new Map(prev).set(currentPhotoType, imageUrl))

        // Add to gallery
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        const captureId = `dreambooth-${galleryKey || gallery.id}-${Date.now()}-${currentPhotoTypeIndex}`
        setSessionState({
          gallery: {
            capturedItems: [
              ...prevItems,
              {
                id: captureId,
                photoUrl: imageUrl,
                photoType: currentPhotoType,
                recordingUrl: captureUrl,
                timestamp: new Date()
              }
            ]
          }
        })

        logActivity(
          `Captured photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${uniquePhotoTypesArray.length})`,
          {
            photoType: currentPhotoType,
            photoTypeIndex: currentPhotoTypeIndex,
            totalPhotos: uniquePhotoTypesArray.length
          }
        )

        // Pre-cache filter variants only when capture is unfiltered (matches classic mode)
        if (applyAfterCapture) {
          void backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, captureId)
        }

        // Move to next photo type or finish
        if (currentPhotoTypeIndex < uniquePhotoTypesArray.length - 1) {
          if (advancedToNextCountdown) {
            // Next countdown was already started from the raw-capture gate; this photo's
            // processing has just finished in the background, so there is nothing else to do.
            return imageUrl
          }
          const nextIndex = currentPhotoTypeIndex + 1

          // CRITICAL: Wait for React to re-render and Preview component to mount before looking for canvas
          await new Promise((resolve) => setTimeout(resolve, 200))

          // Re-create recorder for next photo (recorder cannot be restarted after stop)
          let recorderReady = true
          if (useRecording) {
            let recCanvas: HTMLCanvasElement | null = null
            let retries = 0
            const maxRetries = 10

            while (!recCanvas && retries < maxRetries) {
              recCanvas =
                recordingCanvasElRef.current ||
                (document.querySelector(
                  'canvas[data-preview-type="camera-recording"]'
                ) as HTMLCanvasElement | null)
              if (!recCanvas) {
                retries++
                if (retries < maxRetries) {
                  console.log(
                    `[FrameBasedCapture] Recording canvas not found for photo ${nextIndex + 1}, retrying... (${retries}/${maxRetries})`
                  )
                  await new Promise((resolve) => setTimeout(resolve, 100))
                }
              } else {
                console.log(
                  `[FrameBasedCapture] Recording canvas found for photo ${nextIndex + 1} after ${retries} retries`
                )
              }
            }

            if (!recCanvas) {
              console.error(
                `[FrameBasedCapture] Recording canvas not found for photo ${nextIndex + 1} after ${maxRetries} retries`
              )
              recorderReady = false
            } else {
              const recCtx = recCanvas.getContext('2d')
              const canvasWidth = recCanvas.width > 0 ? recCanvas.width : canvasDimensions?.width ?? 0
              const canvasHeight = recCanvas.height > 0 ? recCanvas.height : canvasDimensions?.height ?? 0

              if (!recCtx || !canvasWidth || !canvasHeight) {
                console.error(
                  `[FrameBasedCapture] Invalid recording canvas for photo ${nextIndex + 1}`
                )
                recorderReady = false
              } else {
                const validatedDims = validateDimensions(canvasWidth, canvasHeight)

                if (captureCanvasRecorderRef.current) {
                  try {
                    if (captureCanvasRecorderRef.current.status === RecorderStatus.Recording) {
                      await captureCanvasRecorderRef.current.stop()
                    }
                  } catch (e) {
                    console.warn('[FrameBasedCapture] Error stopping old recorder:', e)
                  }
                  captureCanvasRecorderRef.current = null
                }

                await new Promise((resolve) => setTimeout(resolve, 100))

                console.log(`[FrameBasedCapture] Creating new recorder for photo ${nextIndex + 1}`, {
                  dimensions: `${validatedDims.width}x${validatedDims.height}`,
                  fps: FPS
                })

                recorderEncoderDimsRef.current = { w: validatedDims.width, h: validatedDims.height }

                try {
                  const newRecorder = new Recorder(recCtx as unknown as RenderingContext, {
                    name: `dreambooth-${galleryKey || gallery.id}-capture-${nextIndex + 1}`,
                    encoderOptions: {
                      codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
                      width: validatedDims.width,
                      height: validatedDims.height,
                      bitrate: 2000000
                    },
                    duration: Infinity,
                    download:
                      downloadLocal &&
                      (userPreferences.recordingType === 'strip_live' ||
                        userPreferences.recordingType === 'strip_story'),
                    frameRate: FPS
                  })
                  captureCanvasRecorderRef.current = newRecorder

                  console.log(`[FrameBasedCapture] Starting recorder for photo ${nextIndex + 1}...`)
                  if (recCanvas.width !== validatedDims.width || recCanvas.height !== validatedDims.height) {
                    console.warn(
                      `[FrameBasedCapture] encoder (${validatedDims.width}x${validatedDims.height}) diverged from recording canvas (${recCanvas.width}x${recCanvas.height}) for photo ${nextIndex + 1}`
                    )
                  }
                  await captureCanvasRecorderRef.current.start()
                  const recorderStatus = captureCanvasRecorderRef.current.status
                  if (recorderStatus !== RecorderStatus.Recording) {
                    console.error(
                      `[FrameBasedCapture] Recorder failed to start for photo ${nextIndex + 1}, status: ${recorderStatus}`
                    )
                    captureCanvasRecorderRef.current = null
                    recorderReady = false
                  } else {
                    console.log(
                      `[FrameBasedCapture] Successfully started recorder for photo ${nextIndex + 1}, status: ${recorderStatus}`
                    )
                  }
                } catch (error) {
                  console.error(
                    `[FrameBasedCapture] Error creating recorder for photo ${nextIndex + 1}:`,
                    error
                  )
                  recorderReady = false
                }
              }
            }
          }

          if (!recorderReady && useRecording) {
            console.warn(`[FrameBasedCapture] Cannot start capture ${nextIndex + 1}: recorder not ready`)
          }

          setCurrentPhotoTypeIndex(nextIndex)
          currentPhotoTypeIndexRef.current = nextIndex

          // Go directly to capture countdown for next photo (no prep countdown)
          setPreparing(false)
          startCaptureCountdown()
        } else {
          logActivity('All photos captured, navigating to retake', {
            totalPhotos: uniquePhotoTypesArray.length
          })
          await stopSessionRecordingAndUpload()
          stateUploader.uploadSessionState()
          navigate(`/${projectId}/retake`)
        }

        return imageUrl
      }

      const deviceType = useCameraStore.getState().deviceType
      const isCameraHardwareFailure =
        cameraSettings.useShutterCapture && deviceType === 'camera' && !imageData

      if (isCameraHardwareFailure) {
        console.log('[FrameBasedCapture] Camera hardware failure detected, triggering recovery')
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity('Camera hardware failure detected, starting recovery (frame-based).', {
          error: 'No image data captured',
          photoTypeIndex: currentPhotoTypeIndex,
          level: 'error',
          recovery: true
        })
        await new Promise((resolve) => setTimeout(resolve, 500))
        void handleCameraRecovery()
        return undefined
      }

      logActivity('Frame-based capture processing failed: No image data captured.', {
        level: 'error',
        photoTypeIndex: currentPhotoTypeIndex
      })
      setIsCaptureError(true)
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setCapturing(false)
      setIsCapturingWithShutter(false)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const deviceType = useCameraStore.getState().deviceType
      const isCameraHardwareFailure =
        cameraSettings.useShutterCapture &&
        deviceType === 'camera' &&
        (errorMessage.includes('LIBUSB') ||
          errorMessage.includes('camera') ||
          errorMessage.includes('device') ||
          errorMessage.includes('USB'))

      if (isCameraHardwareFailure) {
        console.log('[FrameBasedCapture] Camera hardware failure in catch, triggering recovery')
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity(`Camera hardware failure (frame-based): ${errorMessage}`, {
          error: errorMessage,
          photoTypeIndex: currentPhotoTypeIndex,
          level: 'error',
          recovery: true
        })
        await new Promise((resolve) => setTimeout(resolve, 500))
        void handleCameraRecovery()
        return undefined
      }

      logActivity(`Capture processing failed: ${errorMessage}`, {
        error: errorMessage,
        level: 'error',
        photoTypeIndex: currentPhotoTypeIndex
      })
      setIsCaptureError(true)
      return undefined
    } finally {
      captureLockRef.current = false
    }
  }

  handleCaptureRef.current = handleCapture

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const handleFinish = (): void => {
    void stopSessionRecordingAndUpload().then(() => {
      logActivity('All capture sessions completed')
      const neutralPhotos = gallery.capturedItems.filter((item) => item.photoUrl)
      const resources = neutralPhotos.map((item) => ({
        photoUrl: item.photoUrl,
        videoUrl: item.recordingUrl || ''
      }))
      console.log('Resources:', resources)
      setSessionState({
        gallery: {
          ...gallery,
          liveModeProcessing: true,
          liveModeUrl: null,
          liveModeLocalUrl: undefined,
          liveModeRemoteUrl: undefined
        }
      })
      backgroundVideoProcessor.processVideo({
        id: `live-mode-${gallery.id}`,
        type: 'live',
        resources,
        frameConfig: getFrameConfig(
          getFrame(userPreferences.frameId || frames[0]?._id || '') || null
        ),
        sessionId: gallery.id,
        userId: userId || '',
        projectId: projectId || '',
        galleryId: gallery.id,
        speed: globalConfig?.pages.capture?.settings?.recordingSpeed || 1,
        fps: 30,
        photoDuration: 0,
        onComplete: (uploadedUrl: string, localUrl?: string) => {
          const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
            uploadedUrl,
            localUrl
          )
          setSessionState({
            gallery: {
              ...gallery,
              liveModeProcessing: false,
              liveModeUrl: playbackUrl || null,
              liveModeLocalUrl: localUrl,
              liveModeRemoteUrl: videoRemoteUrl
            }
          })
        },
        onError: (error) => {
          setSessionState({
            gallery: {
              ...gallery,
              liveModeProcessing: false,
              liveModeUrl: null,
              liveModeLocalUrl: undefined,
              liveModeRemoteUrl: undefined
            }
          })
          logActivity('Live mode video generation failed', { error: error.message })
        }
      })
      stateUploader.uploadSessionState()
      navigate(`/${projectId}/retake`)
    })
  }

  // Display progress
  const captureProgress = {
    current: capturedPhotosByType.size || gallery.capturedItems?.length || 0,
    total: captureCount
  }

  // Calculate scale for frame display using actual screenSize
  const screenWidth = layoutCanvasW
  const screenHeight = layoutCanvasH
  const frameWidth = selectedFrame.drawParams.canvasWidth
  const frameHeight = selectedFrame.drawParams.canvasHeight

  // Scale frame to fit screen while maintaining aspect ratio
  // Account for padding, top gap, and bottom whitespace for buttons
  const frameChromePortraitTight = !isLandscape && (isCompact || isTiny)
  const frameSessionTimerCompact = isCompact || isTiny
  const paddingSize = frameChromePortraitTight
    ? Math.max(screenWidth * 0.02, 10)
    : Math.max(screenWidth * 0.015, 16)
  const topGap = frameChromePortraitTight
    ? Math.max(screenHeight * 0.032, 32)
    : Math.max(screenHeight * 0.05, 80)
  const buttonAreaHeight = frameChromePortraitTight
    ? Math.max(screenHeight * 0.075, 62)
    : Math.max(screenHeight * 0.15, 180)
  const availableWidth = screenWidth - paddingSize * 2
  const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
  const scale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)

  // Shared session timer position (large screens keep inset; compact uses fixed safe inset)
  const countdownTop = Math.max(screenHeight * 0.02, 32)
  const countdownRight = Math.max(screenWidth * 0.02, 32)
  const countdownFontSize = Math.min(Math.max(screenHeight * 0.022, 22), 34)

  const prepTitleClass = isTiny
    ? 'text-[36px]'
    : isCompact
      ? 'text-[56px]'
      : 'text-[90px]'
  const prepSessionLineClass = isTiny ? 'text-base' : isCompact ? 'text-xl' : 'text-2xl'
  /** Match Capture.tsx prep overlay: monospace + tabular digits for the time */
  const prepCountdownNumClass = isTiny
    ? 'font-mono text-xl tabular-nums'
    : isCompact
      ? 'font-mono text-2xl tabular-nums'
      : 'font-mono text-4xl tabular-nums'
  const prepSubtextClass = isTiny ? 'text-sm' : isCompact ? 'text-base' : 'text-lg'
  const prepButtonClass = isTiny
    ? 'w-[180px] py-3 rounded-2xl text-lg font-bold'
    : isCompact
      ? 'w-[220px] py-4 rounded-2xl text-xl font-bold'
      : 'w-[300px] py-6 rounded-2xl text-3xl font-bold'

  return (
    <div className="relative w-full h-full bg-black">
      <div id="flash" className="w-full h-full flash"></div>

      {isCapturingWithShutter && (
        <div className="absolute inset-0 bg-white/50 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-black mb-4">
              {'\u{1F4F8}'} {t('capture.lookAtCamera', 'Look at the Camera')}
            </h2>
            <p className="text-xl text-black/80">
              {t('capture.stayStill', 'Please stay still while we capture your photo...')}
            </p>
          </div>
        </div>
      )}

      {/* Shared session timer — compact chip matches Filter / other view pages */}
      <div
        className={`absolute z-50 ${frameSessionTimerCompact ? 'right-3 top-3 sm:right-4 sm:top-4' : ''}`}
        style={frameSessionTimerCompact ? undefined : { top: `${countdownTop}px`, right: `${countdownRight}px` }}
      >
        {frameSessionTimerCompact ? (
          <div className="rounded-lg border border-white/35 bg-black/60 px-2.5 py-1.5 text-center shadow-lg backdrop-blur-sm">
            <div className="text-[10px] font-medium uppercase tracking-wide text-white/75">
              {t('capture.timeRemaining', 'Time remaining')}
            </div>
            <div className="font-mono text-base font-semibold tabular-nums leading-none text-white drop-shadow-md">
              {sharedRemainingTime}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-white/90 bg-black/40 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4">
            <div className="mb-1 text-sm text-white/90">{t('capture.timeRemaining', 'Time remaining')}</div>
            <div
              className="font-bold font-mono tabular-nums text-white"
              style={{ fontSize: `${countdownFontSize}px` }}
            >
              {sharedRemainingTime}
            </div>
          </div>
        )}
      </div>

      {isLandscape ? (
        // Landscape Mode: Left big preview + Right frame strip
        <div className="w-full h-full flex">
          {/* Left Area - Big Camera Preview */}
          <div className="w-2/3 relative">
            {/* Capture Countdown Overlay */}
            {capturing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <span
                  key={`countdown-${countdownKey}-${captureCountdown}`}
                  className="text-[200px] text-white animate-countdown drop-shadow-lg"
                >
                  {captureCountdown}
                </span>
              </div>
            )}

            {/* Get Ready Overlay */}
            {preparing && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
                <div
                  className={`flex flex-col items-center max-w-2xl text-center px-4 sm:px-8 ${isTiny ? 'gap-4' : isCompact ? 'gap-6' : 'gap-8'}`}
                >
                  <h1
                    className={`${prepTitleClass} text-center bg-gradient-to-r text-white bg-clip-text text-transparent font-bold leading-tight`}
                  >
                    {t('capture.getReady', 'Get Ready!')}
                  </h1>
                  <p className={`${prepSessionLineClass} text-white/80`}>
                    {t('capture.sessionStarting', 'Session will start in')}{' '}
                    <span className={`${prepCountdownNumClass} font-bold text-white`}>
                      {prepCountdownHook.getFormattedRemainingSeconds()}
                    </span>
                  </p>
                  <div className="flex flex-col items-center gap-3">
                    <Button
                      onClick={startCaptureSession}
                      text={t('capture.startNow', 'Start Now')}
                      className={`${prepButtonClass} transition-all duration-300 ${
                        !canvasDimensions ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                      }`}
                      disabled={!canvasDimensions}
                    />
                    <span className={`text-white/60 ${prepSubtextClass}`}>
                      {t('capture.orWaitCountdown', 'or wait for the countdown')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Capture Error Message */}
            {isCaptureError && (
              <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-4xl font-bold text-red-500 mb-4">
                    {t('capture.captureFailed', 'Capture Failed')}
                  </h2>
                  <p className="text-xl text-white/80 mb-6">
                    {t('capture.stayStillCapturing', 'Please stay still when capturing')}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCaptureError(false)
                      startCaptureCountdown()
                    }}
                    className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3 text-white"
                  >
                    {t('common.retry', 'Retry')}
                  </button>
                </div>
              </div>
            )}

            {/* Completion Controls (interactive frame preview lives on the right) */}
            {allCapturesComplete && !capturing && (
              <div
                className={`absolute z-[6] flex items-center ${frameSessionTimerCompact ? 'bottom-4 left-4 gap-2' : 'bottom-8 left-8 gap-4'}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    const willMirrorAll = uniquePhotoTypesArray.some(
                      (photoType) => !mirroredPhotoTypes.has(photoType)
                    )
                    const next = new Set(mirroredPhotoTypes)
                    uniquePhotoTypesArray.forEach((photoType) => {
                      if (willMirrorAll) next.add(photoType)
                      else next.delete(photoType)
                    })
                    setIsAllMirrored(willMirrorAll)
                    setMirroredPhotoTypes(next)
                  }}
                  className={`bg-white/10 hover:bg-white/20 border border-white/35 font-medium transition-all duration-200 cursor-pointer text-white rounded-full flex items-center shadow-md ${
                    frameSessionTimerCompact ? 'gap-1.5 px-3 py-2 text-sm' : 'gap-3 border-2 px-6 py-3'
                  }`}
                  style={{ opacity: isAllMirrored ? 1 : 0.9 }}
                >
                  <FaExchangeAlt className={frameSessionTimerCompact ? 'h-3.5 w-3.5' : ''} />
                  <span>{t('select.mirror', 'Mirror')}</span>
                </button>
                <button
                  onClick={handleFinish}
                  className={`hover:opacity-90 transition-opacity bg-white rounded-lg font-semibold text-gray-800 shadow-lg ${
                    frameSessionTimerCompact ? 'px-4 py-2 text-base' : 'px-8 py-4 text-2xl'
                  }`}
                >
                  Next →
                </button>
              </div>
            )}

            {/* Camera Preview */}
            <div className="w-full h-full">
              <Preview
                key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${previewHighlightRatio ?? 'auto'}`}
                adjustments={filterAdjustments}
                ratio={previewHighlightRatio}
                cameraRatio={getNormalizedCaptureRatio()}
                cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                filter={selectedFilter?._id}
                postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                onCanvasReady={setCanvasDimensions}
                onRecordingCanvasReady={onRecordingCanvasReady}
                isPaused={isPreviewPaused}
                captureShareRole="producer"
                captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
              />
            </div>
          </div>

          {/* Right Area - Frame Strip */}
          <div className="w-1/3 relative bg-black">
            {/* Frame Strip */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ padding: `${paddingSize}px` }}
            >
              <div
                className="relative flex items-center justify-center"
                style={{
                  width: `${frameWidth * scale * 0.95}px`,
                  height: `${frameHeight * scale * 0.95}px`
                }}
              >
                {allCapturesComplete && !capturing ? (
                  <PrintPreview
                    frameConfig={getFrameConfig(selectedFrame)}
                    selectedPhotos={uniquePhotoTypesArray
                      .map((photoType) => {
                        const url = capturedPhotosByType.get(photoType) || ''
                        if (!url) return ''
                        return mirroredPhotoTypes.has(photoType) ? `mirror:${url}` : url
                      })
                      .filter(Boolean)}
                    isInteractive={true}
                    sessionId={gallery.id}
                  />
                ) : (
                  <FrameOrderedLiveLayers
                    drawParamsImages={selectedFrame.drawParams.images}
                    thumbnailUrl={selectedFrame.thumbnail}
                    frameBackgroundColor={selectedFrame.drawParams.backgroundColor || '#FFFFFF'}
                    scale={scale}
                    scaleMultiplier={0.95}
                    uniquePhotoTypesArray={uniquePhotoTypesArray}
                    capturedPhotosByType={capturedPhotosByType}
                    filterAdjustments={filterAdjustments}
                    selectedFilterId={selectedFilter?._id}
                    postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                    cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                    getPhotoRatio={getPhotoRatio}
                    captureFlow
                    currentPhotoTypeIndex={currentPhotoTypeIndex}
                    livePreviewPaused={isPreviewPaused}
                    cameraRatio={getNormalizedCaptureRatio()}
                    captureShareRole="consumer"
                    captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                  />
                )}
              </div>
            </div>
            
            {/* Progress Counter */}
            <div
              className="absolute z-10"
              style={{
                bottom: `${frameSessionTimerCompact ? 12 : Math.max(screenHeight * 0.02, 32)}px`,
                right: `${frameSessionTimerCompact ? 12 : Math.max(screenWidth * 0.03, 48)}px`
              }}
            >
              <div
                className={`rounded-lg border border-white/25 bg-black/70 text-center shadow-md backdrop-blur-sm ${
                  frameSessionTimerCompact ? 'px-2 py-1.5' : 'border-2 border-white/20 p-4'
                }`}
              >
                <div
                  className={`text-white/80 ${frameSessionTimerCompact ? 'mb-0 text-[10px] uppercase tracking-wide' : 'mb-1 text-sm'}`}
                >
                  Progress
                </div>
                <div
                  className={`font-bold tabular-nums text-white ${frameSessionTimerCompact ? 'text-sm' : ''}`}
                  style={
                    frameSessionTimerCompact
                      ? undefined
                      : { fontSize: `${Math.max(screenHeight * 0.02, 24)}px` }
                  }
                >
                  {captureProgress.current}/{captureProgress.total}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Portrait Mode: Centered frame strip only
        <div
          className="relative w-full h-full flex flex-col overflow-hidden"
          style={{ padding: `${paddingSize}px` }}
        >
          {/* Frame Preview Area - Takes available space */}
          <div
            className="flex-1 min-h-0 relative flex items-center justify-center"
            style={{ paddingTop: `${topGap}px` }}
          >
            {/* Hidden full-size producer: same capture-share + recording canvas as landscape main Preview */}
            <div className="absolute inset-0 z-0 opacity-0 pointer-events-none" aria-hidden>
              <Preview
                key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${previewHighlightRatio ?? 'auto'}`}
                adjustments={filterAdjustments}
                ratio={previewHighlightRatio}
                cameraRatio={getNormalizedCaptureRatio()}
                cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                filter={selectedFilter?._id}
                postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                onCanvasReady={setCanvasDimensions}
                onRecordingCanvasReady={onRecordingCanvasReady}
                isPaused={isPreviewPaused}
                captureShareRole="producer"
                captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
              />
            </div>
            {/* Full frame strip — consumers reuse producer blob */}
            <div
              className="absolute z-[1] flex items-center justify-center"
              style={{
                width: `${frameWidth * scale}px`,
                height: `${frameHeight * scale}px`
              }}
            >
              {allCapturesComplete && !capturing ? (
                <PrintPreview
                  frameConfig={getFrameConfig(selectedFrame)}
                  selectedPhotos={uniquePhotoTypesArray
                    .map((photoType) => {
                      const url = capturedPhotosByType.get(photoType) || ''
                      if (!url) return ''
                      return mirroredPhotoTypes.has(photoType) ? `mirror:${url}` : url
                    })
                    .filter(Boolean)}
                  isInteractive={true}
                  sessionId={gallery.id}
                />
              ) : (
                <FrameOrderedLiveLayers
                  drawParamsImages={selectedFrame.drawParams.images}
                  thumbnailUrl={selectedFrame.thumbnail}
                  frameBackgroundColor={selectedFrame.drawParams.backgroundColor || '#FFFFFF'}
                  scale={scale}
                  uniquePhotoTypesArray={uniquePhotoTypesArray}
                  capturedPhotosByType={capturedPhotosByType}
                  filterAdjustments={filterAdjustments}
                  selectedFilterId={selectedFilter?._id}
                  postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                  cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  getPhotoRatio={getPhotoRatio}
                  captureFlow
                  currentPhotoTypeIndex={currentPhotoTypeIndex}
                  livePreviewPaused={isPreviewPaused}
                  cameraRatio={getNormalizedCaptureRatio()}
                  captureShareRole="consumer"
                  captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                />
              )}
            </div>
          </div>

          {/* Prep Countdown Overlay */}
          {preparing && (
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
              <div
                className={`flex flex-col items-center max-w-2xl text-center px-4 sm:px-8 ${isTiny ? 'gap-4' : isCompact ? 'gap-6' : 'gap-8'}`}
              >
                <h1
                  className={`${prepTitleClass} text-center bg-gradient-to-r text-white bg-clip-text text-transparent font-bold leading-tight`}
                >
                  {t('capture.getReady', 'Get Ready!')}
                </h1>
                <p className={`${prepSessionLineClass} text-white/80`}>
                  {t('capture.sessionStarting', 'Session will start in')}{' '}
                  <span className={`${prepCountdownNumClass} font-bold text-white`}>
                    {prepCountdownHook.getFormattedRemainingSeconds()}
                  </span>
                </p>
                <div className="flex flex-col items-center gap-3">
                  <Button
                    onClick={startCaptureSession}
                    text={t('capture.startNow', 'Start Now')}
                    className={`${prepButtonClass} transition-all duration-300 ${
                      !canvasDimensions ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                    }`}
                    disabled={!canvasDimensions}
                  />
                  <span className={`text-white/60 ${prepSubtextClass}`}>
                    {t('capture.orWaitCountdown', 'or wait for the countdown')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Capture Countdown Overlay */}
          {capturing && (
            <div className="absolute inset-0 z-[4] flex items-center justify-center">
              <span
                key={`countdown-${countdownKey}-${captureCountdown}`}
                className="text-white animate-countdown"
                style={{
                  fontSize: `${frameChromePortraitTight ? Math.min(Math.max(screenHeight * 0.12, 64), 120) : Math.max(screenHeight * 0.18, 200)}px`
                }}
              >
                {captureCountdown}
              </span>
            </div>
          )}

          {/* Capture Error Message */}
          {isCaptureError && (
            <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
              <div className="text-center">
              <h2 className="text-4xl font-bold text-red-500 mb-4">
                {t('capture.captureFailed', 'Capture Failed')}
              </h2>
              <p className="text-xl text-white/80 mb-6">
                {t('capture.stayStillCapturing', 'Please stay still when capturing')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setIsCaptureError(false)
                  startCaptureCountdown()
                }}
                className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3"
                style={{
                  fontSize: `${Math.max(screenHeight * 0.018, 18)}px`,
                  color: '#ffffff'
                }}
              >
                {t('common.retry', 'Retry')}
              </button>
              </div>
            </div>
          )}

          {allCapturesComplete && !capturing && (
            <div
              className={`absolute z-[6] flex items-center ${frameChromePortraitTight ? 'bottom-3 left-3 gap-2' : 'bottom-8 left-8 gap-4'}`}
            >
              <button
                type="button"
                onClick={() => {
                  const willMirrorAll = uniquePhotoTypesArray.some(
                    (photoType) => !mirroredPhotoTypes.has(photoType)
                  )
                  const next = new Set(mirroredPhotoTypes)
                  uniquePhotoTypesArray.forEach((photoType) => {
                    if (willMirrorAll) next.add(photoType)
                    else next.delete(photoType)
                  })
                  setIsAllMirrored(willMirrorAll)
                  setMirroredPhotoTypes(next)
                }}
                className={`bg-white/10 hover:bg-white/20 border border-white/35 font-medium transition-all duration-200 cursor-pointer text-white rounded-full flex items-center shadow-md ${
                  frameChromePortraitTight ? 'gap-1.5 px-3 py-2 text-sm' : 'gap-3 border-2 px-6 py-3'
                }`}
                style={{ opacity: isAllMirrored ? 1 : 0.9 }}
              >
                <FaExchangeAlt className={frameChromePortraitTight ? 'h-3.5 w-3.5' : ''} />
                <span>{t('select.mirror', 'Mirror')}</span>
              </button>
              <button
                onClick={handleFinish}
                className={`hover:opacity-90 transition-opacity bg-white rounded-lg font-semibold text-gray-800 shadow-lg ${
                  frameChromePortraitTight ? 'px-4 py-2 text-base' : 'px-8 py-4 text-2xl'
                }`}
              >
                Next →
              </button>
            </div>
          )}

          {/* Button Area - Fixed height, no overlap */}
          <div className="flex-shrink-0 relative" style={{ height: `${buttonAreaHeight}px` }}>
            {/* Progress Indicator */}
            <div
              className="absolute z-10"
              style={{
                bottom: `${frameChromePortraitTight ? 12 : Math.max(screenHeight * 0.02, 32)}px`,
                right: `${frameChromePortraitTight ? 12 : Math.max(screenWidth * 0.03, 48)}px`
              }}
            >
              <div
                className={`rounded-lg border border-white/25 bg-black/70 text-center shadow-md backdrop-blur-sm ${
                  frameChromePortraitTight ? 'px-2 py-1.5' : 'border-2 border-white/20 p-4'
                }`}
              >
                <div
                  className={`text-white/80 ${frameChromePortraitTight ? 'mb-0 text-[10px] uppercase tracking-wide' : 'mb-1 text-sm'}`}
                >
                  Progress
                </div>
                <div
                  className={`font-bold tabular-nums text-white ${frameChromePortraitTight ? 'text-sm' : ''}`}
                  style={
                    frameChromePortraitTight
                      ? undefined
                      : { fontSize: `${Math.max(screenHeight * 0.02, 24)}px` }
                  }
                >
                  {Math.min(currentPhotoTypeIndex + 1, captureCount)}/{captureCount}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FrameBasedCapture

